import { Injectable } from '@angular/core';
import { CONSTANTS } from '../../Constants/Constants';
import { SellPriceConfig, TaxTypes, TaxCostSources, TaxCalculationResult } from '../../models/sol-estimate-table/SellPriceConfig';
import { TaxSettingsModel } from '../../models/dtos/Classes/TaxSettingsModel';
import { TaxRateHelper } from '../../models/dtos/Classes/TaxRateHelper';
import { EscalationData, EstimateTab, SQWebProject } from '../../models/dtos';
import { SubSidyProgramMaterialType, TaxRateIndex } from '../../models/enum';
import { CommonDataService } from '../common-data.service';
import { CommonMetaDataService } from '../common-meta-data.service';
import { EstimateCommonFunctionsService } from './estimate-common-functions.service';
import { TranslateService } from '@ngx-translate/core';
import {
  EstimateUiModel,
  EstimateTaxRateModel,
  createDefaultTaxRateRowModel,
  TAX_RATE_ROW_NAMES,
} from '../../models/sol-estimate-table/ui-models/index';
import { roundHalfEven9 } from '../../../types/number-extensions';

/**
 * Handles all tax-related logic for the estimate tab.
 * Works exclusively with EstimateUiModel — no FormGroup references.
 */
@Injectable({
  providedIn: 'root'
})
export class SolEstimateTaxService {

  /** Tax row visibility flags derived from the TaxTypeID. */
  taxVisibility = {
    showTaxNotIncluded: false,
    showTaxMaterialRate: false,
    showTaxMaterialLaborRate: false
  };

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly estimateCommonFunctions: EstimateCommonFunctionsService,
    private readonly translate: TranslateService
  ) { }

  // ── Visibility ─────────────────────────────────────────────────

  updateTaxVisibility(taxTypeID: number): void {
    this.taxVisibility = this.getTaxVisibility(taxTypeID);
  }

  // ── Patching (initial load) ────────────────────────────────────

  patchTaxCosts(
    model: EstimateUiModel,
    config: SellPriceConfig,
    subTotalsCost: number,
    sqWebProject?: SQWebProject
  ): void {
    if (this.skipTaxForHiddenProject(model)) {
      return;
    }
    const taxTypeID = config.taxTypeID;
    this.resetInactiveTaxRows(model, taxTypeID);

    switch (taxTypeID) {
      case TaxTypes.SalesTax:
        this.patchSalesTax(model, config, subTotalsCost);
        break;

      case TaxTypes.UseTax:
      case TaxTypes.HST:
      case TaxTypes.PST:
        this.patchUseTax(model, config, subTotalsCost);
        break;

      case TaxTypes.NoTax:
      default:
        // Use SQWebProject taxCategory sumUpCost
        this.patchTaxNotIncludedCost(model, sqWebProject.taxCategory.sumUpCost, config, subTotalsCost);
        break;
    }
  }

  // ── Rate Update ─────────────────────────────────────────────────

  /**
   * Updates the tax rate in the metadata (taxRateCollection) so that
   * subsequent recalculations use the user-entered rate.
   */
  updateTaxRateInMetadata(rowIndex: number, newRate: number): void {
    const otherMetaData = this.commonDataService.getOtherMetaData();
    if (otherMetaData?.taxRateCollection?.[rowIndex]) {
      otherMetaData.taxRateCollection[rowIndex].taxRate = newRate;
    }
  }

  // ── Recalculation ──────────────────────────────────────────────

  recalculateTaxCosts(model: EstimateUiModel, config: SellPriceConfig): void {
    if (this.skipTaxForHiddenProject(model)) {
      return;
    }
    const stc = model.subTotals.cost;
    const taxTypeID = config.taxTypeID;
    this.resetInactiveTaxRows(model, taxTypeID);

    switch (taxTypeID) {
      case TaxTypes.SalesTax:
        this.patchSalesTax(model, config, stc);
        break;
      case TaxTypes.UseTax:
        this.patchUseTax(model, config, stc);
        break;
      case TaxTypes.HST:
      case TaxTypes.PST:
        this.patchUseTax(model, config, stc);
        break;
      case TaxTypes.NoTax:
      default:
        {
          const row = model.taxNotIncluded;
          const cost = row.cost;
          const presentGMPercent = row.grossMarginPercentage;
          const gmPercent = this.estimateCommonFunctions.resolveRowGMPercent(config, Number.isNaN(presentGMPercent) ? undefined : presentGMPercent);
          const result = this.estimateCommonFunctions.calculateRowSellPrice(cost, gmPercent, config.isMarkUpSelected);
          this.estimateCommonFunctions.updateRowResult(row, result, stc);
        }
        break;
    }
  }

  // ── Hidden Project ─────────────────────────────────────────────

  /** A hidden project has no tax: zeroes every tax amount and returns true so callers skip calculation. */
  private skipTaxForHiddenProject(model: EstimateUiModel): boolean {
    if (!this.commonDataService.getRootNodeHidden()) {
      return false;
    }

    this.clearTaxRateAmounts(model.taxMaterialRate);
    this.clearTaxRateAmounts(model.taxMaterialLaborRate);
    model.taxNotIncluded.cost = CONSTANTS.ZERO;
    model.taxNotIncluded.sellPrice = CONSTANTS.ZERO;
    model.taxNotIncluded.tdcPercentage = CONSTANTS.ZERO;
    model.taxNotIncluded.grossMargin = CONSTANTS.ZERO;
    return true;
  }

  // ── Tax Not Included ───────────────────────────────────────────

  /**
   * Zeroes the tax section that does not apply to the current tax type.
   * Must run before misc/risk, which reads the Use Tax cost for the auto-risk base.
   */
  resetInactiveTaxRows(model: EstimateUiModel, taxTypeID: number): void {
    const isCostTax = taxTypeID === TaxTypes.UseTax || taxTypeID === TaxTypes.HST || taxTypeID === TaxTypes.PST;
    const isSellPriceTax = taxTypeID === TaxTypes.SalesTax || taxTypeID === TaxTypes.BOTax;

    if (!isCostTax) {
      this.clearTaxRateAmounts(model.taxMaterialRate);
    }
    if (!isSellPriceTax) {
      this.clearTaxRateAmounts(model.taxMaterialLaborRate);
    }
  }

  /** Clears amounts but keeps the configured rates for display. */
  private clearTaxRateAmounts(taxRate: EstimateTaxRateModel): void {
    taxRate.headerRow.cost = CONSTANTS.ZERO;
    taxRate.headerRow.sellPrice = CONSTANTS.ZERO;
    taxRate.headerRow.tdcPercentage = CONSTANTS.ZERO;

    for (const row of taxRate.taxRateRows) {
      row.cost = CONSTANTS.ZERO;
      row.sellPrice = CONSTANTS.ZERO;
      row.tdcPercentage = CONSTANTS.ZERO;
    }
  }

  private patchTaxNotIncludedCost(
    model: EstimateUiModel,
    value: number,
    config: SellPriceConfig,
    subTotalsCost: number
  ): void {
    this.estimateCommonFunctions.updateStandaloneRowWithSellPrice(model.taxNotIncluded, value, config, subTotalsCost);
  }

  // ── Use Tax ────────────────────────────────────────────────────

  private patchUseTax(model: EstimateUiModel, config: SellPriceConfig, subTotalsCost: number): void {
    model.taxMaterialRate.headerRow.description = this.getTaxHeaderDescription(config.taxTypeID);

    const otherMetaData = this.commonDataService.getOtherMetaData();
    const taxSettings: TaxSettingsModel | undefined = otherMetaData?.taxSettings;
    const taxRateCollection: TaxRateHelper[] = otherMetaData?.taxRateCollection ?? [];

    if (!taxSettings || taxRateCollection.length === 0) return;

    const costSources = this.collectTaxCostSources(model);

    // Dynamically rebuild rows from TaxRateCollection.
    model.taxMaterialRate.taxRateRows = [];
    for (const taxItem of taxRateCollection) {
      const row = createDefaultTaxRateRowModel(
        taxItem.description || TAX_RATE_ROW_NAMES.material,
        taxItem.taxRate
      );
      model.taxMaterialRate.taxRateRows.push(row);
    }

    let totalCost = CONSTANTS.ZERO;
    let totalSellPrice = CONSTANTS.ZERO;

    for (let i = 0; i < model.taxMaterialRate.taxRateRows.length; i++) {
      const row = model.taxMaterialRate.taxRateRows[i];
      const taxItem = taxRateCollection[i];

      const result = this.calculateUseTaxItem(
        config.taxTypeID,
        taxItem,
        taxSettings,
        costSources
      );

      totalCost += result.taxCost;
      totalSellPrice += result.taxSellPrice;

      row.rate = taxItem.taxRate;
      row.cost = result.taxCost;
      row.sellPrice = result.taxSellPrice;
      row.tdcPercentage = this.estimateCommonFunctions.calculateTdcPercent(result.taxCost, subTotalsCost);
    }

    // Update header totals.
    model.taxMaterialRate.headerRow.cost = totalCost;
    model.taxMaterialRate.headerRow.sellPrice = totalSellPrice;
    model.taxMaterialRate.headerRow.tdcPercentage = this.estimateCommonFunctions.calculateTdcPercent(totalCost, subTotalsCost);

  }

  // ── Sales Tax ──────────────────────────────────────────────────

  private patchSalesTax(model: EstimateUiModel, config: SellPriceConfig, subTotalsCost: number): void {
    const otherMetaData = this.commonDataService.getOtherMetaData();
    const taxSettings: TaxSettingsModel | undefined = otherMetaData?.taxSettings;
    const taxRateCollection: TaxRateHelper[] = otherMetaData?.taxRateCollection ?? [];

    if (!taxSettings || taxRateCollection.length === 0) return;

    const { materialBase, laborBase } = this.collectSalesTaxBases(model, 'sellPrice');
    // soldAsGMPercent holds MU% in markup mode, which legitimately exceeds 100.
    const soldAsGMPercent = config.isMarkUpSelected
      ? this.estimateCommonFunctions.getGrossMarginPercent(config.soldAsGMPercent)
      : config.soldAsGMPercent;

    let materialTaxCost = CONSTANTS.ZERO;
    let materialTaxSellPrice = CONSTANTS.ZERO;
    let laborTaxCost = CONSTANTS.ZERO;
    let laborTaxSellPrice = CONSTANTS.ZERO;

    // Looked up by position, not description: descriptions are localized and can be empty or differ from the UI language.
    const materialTaxItem = taxRateCollection[TaxRateIndex.Material];
    const laborTaxItem = taxRateCollection[TaxRateIndex.Labor];

    const materialDisplayRate = materialTaxItem?.taxRate ?? CONSTANTS.ZERO;
    const laborDisplayRate = laborTaxItem?.taxRate ?? CONSTANTS.ZERO;

    if (materialTaxItem) {
      const materialResult = this.calculateSalesTaxAmount(materialTaxItem, materialBase, soldAsGMPercent);
      materialTaxCost += materialResult.taxCost;
      materialTaxSellPrice += materialResult.taxSellPrice;
    }

    if (laborTaxItem) {
      const laborResult = this.calculateSalesTaxAmount(laborTaxItem, laborBase, soldAsGMPercent);
      laborTaxCost += laborResult.taxCost;
      laborTaxSellPrice += laborResult.taxSellPrice;
    }

    // Build exactly 2 rows: MATERIAL_RATE and LABOR_RATE.
    const materialRow = createDefaultTaxRateRowModel(TAX_RATE_ROW_NAMES.material, materialDisplayRate);
    materialRow.cost = materialTaxCost;
    materialRow.sellPrice = materialTaxSellPrice;
    materialRow.tdcPercentage = this.estimateCommonFunctions.calculateTdcPercent(materialTaxCost, subTotalsCost);

    const laborRow = createDefaultTaxRateRowModel(TAX_RATE_ROW_NAMES.labor, laborDisplayRate);
    laborRow.cost = laborTaxCost;
    laborRow.sellPrice = laborTaxSellPrice;
    laborRow.tdcPercentage = this.estimateCommonFunctions.calculateTdcPercent(laborTaxCost, subTotalsCost);

    model.taxMaterialLaborRate.taxRateRows = [materialRow, laborRow];

    const totalCost = materialTaxCost + laborTaxCost;
    const totalSellPrice = materialTaxSellPrice + laborTaxSellPrice;

    // Update header totals.
    model.taxMaterialLaborRate.headerRow.description = this.getTaxHeaderDescription(config.taxTypeID);
    model.taxMaterialLaborRate.headerRow.cost = totalCost;
    model.taxMaterialLaborRate.headerRow.sellPrice = totalSellPrice;
    model.taxMaterialLaborRate.headerRow.tdcPercentage = this.estimateCommonFunctions.calculateTdcPercent(totalCost, subTotalsCost);
  }

  // ── Tax Source Collectors ──────────────────────────────────────

  collectTaxCostSources(model: EstimateUiModel): TaxCostSources {
    const mat = model.materialCosts;
    const lab = model.laborCosts;
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const getNamedCost = (name: string): number => {
      const row = this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, name);
      return row ? row.cost : CONSTANTS.ZERO;
    };

    const totalWarrantyCost =
      mat.siemensMaterial.warranty.cost +
      mat.outsideMaterial.warranty.cost +
      mat.mechanicalMaterial.warranty.cost +
      mat.electricalMaterial.warranty.cost;

    const totalShippingCost =
      mat.siemensMaterial.shippingAndHandling.cost +
      mat.outsideMaterial.shippingAndHandling.cost +
      mat.mechanicalMaterial.shippingAndHandling.cost +
      mat.electricalMaterial.shippingAndHandling.cost;

    let subcontractorCostTotal = CONSTANTS.ZERO;
    for (const row of model.subContractorCosts.subContractorsCostsRow) {
      subcontractorCostTotal += row.cost;
    }

    const siemensMaterialCost = mat.siemensMaterial.headerRow.cost;
    const outsideMaterialCost = mat.outsideMaterial.headerRow.cost;
    const mechanicalMaterialCost = mat.mechanicalMaterial.headerRow.cost;
    const electricalMaterialCost = mat.electricalMaterial.headerRow.cost;

    return {
      siemensMaterialCost,
      outsideMaterialCost,
      mechanicalMaterialCost,
      electricalMaterialCost,
      totalWarrantyCost,
      totalShippingCost,

      projectManagementLaborCost: lab.projectManagement.cost,
      engineeringLaborCost: lab.engineering.cost,
      networkEngineeringLaborCost: lab.networkEngineering.cost,
      qualificationEngineeringLaborCost: lab.qualificationEngineering.cost,
      cyberSecurityEngineeringLaborCost: lab.cyberSecurityEngineering.cost,
      energyEngineeringLaborCost: lab.energyEngineering.cost,
      electricalEngineeringLaborCost: lab.electricalEngineering.cost,
      graphicsLaborCost: lab.graphics.cost,
      programmingLaborCost: lab.programming.cost,
      specialistLaborCost: lab.specialist.cost,
      digitalDeploymentLaborCost: lab.digitalDeploymentSpecialist.cost,
      mechanicalLaborCost: lab.mechanical.cost,
      electricianLaborCost: lab.electrical.cost,

      miscCost: getNamedCost('MISCELLANEOUS_COSTS'),
      travelExpenseCost: getNamedCost('TRAVEL_EXPENSE_COSTS'),
      riskCost: getNamedCost('PROJECT_ENTERED_RISK_COSTS'),
      autoRiskCost: getNamedCost('CALCULATED_RISK_COSTS'),
      productivityReserveCost: getNamedCost('PRODUCTIVITY_RESERVE'),

      subcontractorCostTotal,
      materialCostTotal: siemensMaterialCost + outsideMaterialCost + mechanicalMaterialCost + electricalMaterialCost + totalShippingCost + totalWarrantyCost
    };
  }

  /**
   * Taxable bases for the material and labor Sales Tax rates, read from either cost or sell price.
   * One rule set feeds both the tax amount ('sellPrice') and Sell-As GM% derivation ('cost'),
   * so the two cannot drift apart.
   */
  collectSalesTaxBases(model: EstimateUiModel, field: 'cost' | 'sellPrice'): { materialBase: number; laborBase: number } {
    const taxSettings = this.commonDataService.getOtherMetaData()?.taxSettings;
    if (!taxSettings) {
      return { materialBase: CONSTANTS.ZERO, laborBase: CONSTANTS.ZERO };
    }

    const mat = model.materialCosts;
    const categories = [mat.siemensMaterial, mat.outsideMaterial, mat.mechanicalMaterial, mat.electricalMaterial];
    const miscRows = model.miscRiskTravelCosts.rows;
    const riskRows = model.miscRiskTravelCosts.riskCosts?.rows ?? null;

    const sum = (values: number[]): number => values.reduce((total, value) => total + value, CONSTANTS.ZERO);
    const named = (name: string): number =>
      this.estimateCommonFunctions.findNamedRowInModel(miscRows, riskRows, name)?.[field] ?? CONSTANTS.ZERO;

    let materialBase = CONSTANTS.ZERO;
    if (taxSettings.salesTaxMaterial) {
      materialBase += sum(categories.map(category => category.headerRow[field]));
    }
    if (taxSettings.salesTaxShippingAndHandling) {
      materialBase += sum(categories.map(category => category.shippingAndHandling[field]));
    }
    if (taxSettings.salesTaxWarranty) {
      materialBase += sum(categories.map(category => category.warranty[field]));
    }
    if (taxSettings.salesTaxMiscAndTravel) {
      let misc = named('MISCELLANEOUS_COSTS');
      this.estimateCommonFunctions.estimateNonTaxableItemsDictionary.forEach(item => {
        misc -= item[field];
      });
      materialBase += misc + named('PROJECT_ENTERED_RISK_COSTS') + named('SUBCONTRACTOR_FEE')
        + named('CALCULATED_RISK_COSTS') + named('PRODUCTIVITY_RESERVE');
    }

    let laborBase = CONSTANTS.ZERO;
    if (taxSettings.salesTaxLabor) {
      laborBase += model.laborCosts.laborCostsHeader[field];
    }
    if (taxSettings.salesTaxMiscAndTravel) {
      laborBase += named('TRAVEL_EXPENSE_COSTS');
    }
    if (taxSettings.salesTaxSubcontractors) {
      laborBase += sum(model.subContractorCosts.subContractorsCostsRow.map(row => row[field]));
    }

    return { materialBase, laborBase };
  }

  // ── Tax Description ──────────────────────────────────────────

  private getTaxHeaderDescription(taxTypeID: number): string {
    const taxTypes = this.commonMetaDataService.getMetaData()?.taxTypes ?? [];
    const description = taxTypes.find(t => t.id === taxTypeID)?.description;
    if (description) {
      return description;
    }

    if (taxTypeID === TaxTypes.HST) {
      return 'HST';
    }
    if (taxTypeID === TaxTypes.PST) {
      return 'PST';
    }

    return this.translate.instant('TAX_MATERIAL_RATE');
  }

  // ── Tax Visibility ──────────────────────────────────────────────

  private getTaxVisibility(taxTypeID: number): { showTaxNotIncluded: boolean; showTaxMaterialRate: boolean; showTaxMaterialLaborRate: boolean } {
    switch (taxTypeID) {
      case TaxTypes.SalesTax:
        return { showTaxNotIncluded: false, showTaxMaterialRate: false, showTaxMaterialLaborRate: true };
      case TaxTypes.UseTax:
      case TaxTypes.HST:
      case TaxTypes.PST:
        return { showTaxNotIncluded: false, showTaxMaterialRate: true, showTaxMaterialLaborRate: false };
      case TaxTypes.NoTax:
      case TaxTypes.BOTax:
      case TaxTypes.GeneralTax:
      default:
        return { showTaxNotIncluded: true, showTaxMaterialRate: false, showTaxMaterialLaborRate: false };
    }
  }

  // ── Tax Calculation Engine ────────────────────────────────────

  private calculateUseTaxItem(
    taxTypeID: number,
    taxItem: TaxRateHelper,
    taxSettings: TaxSettingsModel,
    costSources: TaxCostSources
  ): TaxCalculationResult {
    let taxCost = CONSTANTS.ZERO;

    if (taxTypeID === TaxTypes.UseTax) {
      let materialCost = CONSTANTS.ZERO;

      if (taxSettings.useTaxMaterial) {
        materialCost += costSources.siemensMaterialCost
          + costSources.outsideMaterialCost
          + costSources.electricalMaterialCost
          + costSources.mechanicalMaterialCost;
      }

      if (taxSettings.useTaxLabor) {
        materialCost += costSources.projectManagementLaborCost
          + costSources.engineeringLaborCost
          + costSources.networkEngineeringLaborCost
          + costSources.qualificationEngineeringLaborCost
          + costSources.cyberSecurityEngineeringLaborCost
          + costSources.energyEngineeringLaborCost
          + costSources.electricalEngineeringLaborCost
          + costSources.specialistLaborCost
          + costSources.mechanicalLaborCost
          + costSources.electricianLaborCost
          + costSources.programmingLaborCost
          + costSources.graphicsLaborCost
          + costSources.digitalDeploymentLaborCost;
      }

      if (taxSettings.useTaxWarranty) {
        materialCost += costSources.totalWarrantyCost;
      }

      if (taxSettings.useTaxShippingAndHandling) {
        materialCost += costSources.totalShippingCost;
      }

      if (taxSettings.useTaxMiscAndTravel) {
        materialCost += costSources.miscCost
          + costSources.travelExpenseCost
          + costSources.riskCost
          + costSources.autoRiskCost
          + costSources.productivityReserveCost;
      }

      if (taxSettings.useTaxSubcontractors) {
        materialCost += costSources.subcontractorCostTotal;
      }

      taxCost = roundHalfEven9((materialCost * taxItem.taxRate) / CONSTANTS.HUNDRED);

    } else if (taxTypeID === TaxTypes.PST || taxTypeID === TaxTypes.HST) {
      const escalatedSubsidy = this.getEscalatedSubsidy();
      taxCost = roundHalfEven9((taxItem.taxRate * (costSources.materialCostTotal + escalatedSubsidy)) / CONSTANTS.HUNDRED);
    }

    return {
      taxCost,
      taxSellPrice: taxCost,
      taxCostVisibility: true,
      taxTDCVisibility: true
    };
  }

  // ── Escalated Subsidy ─────────────────────────────────────────
  private getEscalatedSubsidy(): number {
    const estimateTab = this.commonDataService.getEstimateMetaData()?.estimateTab;
    const subsidy = Number(estimateTab?.subsidy ?? CONSTANTS.ZERO);
    if (subsidy <= CONSTANTS.ZERO) {
      return CONSTANTS.ZERO;
    }

    const escalationData = this.getSubsidyEscalationData(estimateTab?.subsidyMaterial);
    if (!escalationData?.escalationEnabled) {
      return subsidy;
    }

    return this.estimateCommonFunctions.calculateTotalEscalatedCost({ ...escalationData, baseCost: subsidy });
  }

  private getSubsidyEscalationData(subsidyMaterial: number): EscalationData | undefined {
    const escalationRates = this.commonDataService.getEstimateMetaData()?.escalationRates;
    switch (subsidyMaterial) {
      case SubSidyProgramMaterialType.Siemens_Material:
        return escalationRates?.siemensMaterialEscalation;
      case SubSidyProgramMaterialType.Outside_Material:
        return escalationRates?.outsideMaterialEscalation;
      case SubSidyProgramMaterialType.Mechanical_Material:
        return escalationRates?.mechanicalMaterialEscalation;
      case SubSidyProgramMaterialType.Electrical_Material:
        return escalationRates?.electricalMaterialEscalation;
      default:
        return undefined;
    }
  }

  private calculateSalesTaxAmount(
    taxItem: TaxRateHelper,
    taxableSellPrice: number,
    soldAsGMPercent: number
  ): TaxCalculationResult {
    if (soldAsGMPercent >= CONSTANTS.HUNDRED) {
      return this.zeroSalesTaxResult();
    }

    const taxCost = roundHalfEven9((taxItem.taxRate * taxableSellPrice) / CONSTANTS.HUNDRED);

    return {
      taxCost,
      taxSellPrice: taxCost,
      taxCostVisibility: false,
      taxTDCVisibility: false
    };
  }

  private zeroSalesTaxResult(): TaxCalculationResult {
    return {
      taxCost: CONSTANTS.ZERO,
      taxSellPrice: CONSTANTS.ZERO,
      taxCostVisibility: false,
      taxTDCVisibility: false
    };
  }
}
